// Formatos y rutas compartidas del admin. Todo en español y en hora de Panamá.

export const PANAMA_TZ = 'America/Panama';

const moneyFmt = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** $1,234.56 (dólares, como se cobra en Panamá). Negativos con signo menos: −$3.50. */
export function money(v: any): string {
  const n = typeof v === 'number' ? v : parseFloat(v ?? '0');
  const x = isFinite(n) ? n : 0;
  return (x < 0 ? '−$' : '$') + moneyFmt.format(Math.abs(x));
}

export function toNum(v: any): number {
  const n = typeof v === 'number' ? v : parseFloat(v ?? '0');
  return isFinite(n) ? n : 0;
}

/** yyyy-MM-dd de una fecha local (lo que el usuario elige en un calendario). */
export function dateKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Hoy en Panamá como yyyy-MM-dd. */
export function todayKey(): string {
  return panamaParts(new Date()).key;
}

function panamaParts(d: Date) {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: PANAMA_TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return { key: `${get('year')}-${get('month')}-${get('day')}`, y: get('year'), m: get('month'), d: get('day'), hh: get('hour') === '24' ? '00' : get('hour'), mm: get('minute') };
}

/**
 * Formatea fechas sin correr el día:
 * - 'YYYY-MM-DD' (columnas date del API) se muestra tal cual, sin pasar por la zona del navegador.
 * - timestamps se muestran en hora de Panamá.
 * mode: 'date' → 30/11/2026 · 'datetime' → 30/11/2026 14:28 · 'short' → 30 nov · 'shortTime' → 30 nov · 14:28
 */
export function panamaDate(value: any, mode: 'date' | 'datetime' | 'short' | 'shortTime' = 'date'): string {
  if (value === null || value === undefined || value === '') return '—';
  const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  let y: string, m: string, d: string, hh = '', mm = '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    [y, m, d] = value.split('-');
  } else {
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return '—';
    const p = panamaParts(date);
    y = p.y; m = p.m; d = p.d; hh = p.hh; mm = p.mm;
  }
  const short = `${parseInt(d, 10)} ${months[parseInt(m, 10) - 1]}`;
  switch (mode) {
    case 'datetime': return `${d}/${m}/${y}${hh ? ` ${hh}:${mm}` : ''}`;
    case 'short': return short;
    case 'shortTime': return hh ? `${short} · ${hh}:${mm}` : short;
    default: return `${d}/${m}/${y}`;
  }
}

/** Días entre hoy (Panamá) y una fecha 'YYYY-MM-DD'. Negativo si ya pasó. */
export function daysUntil(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  const a = Date.parse(todayKey() + 'T12:00:00Z');
  const b = Date.parse(String(dateStr).slice(0, 10) + 'T12:00:00Z');
  return Math.round((b - a) / 86400000);
}

/** "≠ 1 = inactivo": los registros viejos pueden tener 0 y los nuevos 2 (D11). */
export function isActive(status: any): boolean {
  return Number(status) === 1;
}

export type EntityType = 'brewery' | 'branch' | 'event' | 'customer' | 'user' | 'reservation' | 'code' | 'affiliate' | 'settlement' | 'payout' | 'payoutRequest';

/** Ruta de la ficha (o pantalla de detalle) de cada tipo de registro. */
export function entityRoute(type: EntityType, id: any, extra?: any): { commands: any[]; queryParams?: any } | null {
  if (id === null || id === undefined || id === '') return null;
  switch (type) {
    case 'brewery': return { commands: ['/breweries', id] };
    case 'branch': return { commands: ['/branches', 'edit', id] };
    case 'event': return { commands: ['/events', id] };
    case 'customer': return { commands: ['/customers', id] };
    case 'user': return { commands: ['/partner-users', 'edit', id] };
    case 'reservation': return { commands: ['/reservations'], queryParams: { open: id } };
    case 'code': return { commands: ['/discount-codes', 'edit', id] };
    case 'affiliate': return { commands: ['/affiliates', 'view', id] };
    case 'settlement': return { commands: ['/branch-settlements', 'detail', id] };
    case 'payout': return { commands: ['/payouts', id] };
    case 'payoutRequest': return { commands: ['/payout-requests', 'detail', id] };
    default: return null;
  }
}

/** Mensajes conocidos del API en inglés → español. */
const TRANSLATIONS: [RegExp, string][] = [
  [/assignments updated successfully/i, 'Categorías y productos guardados.'],
  [/error updating assignments/i, 'No se pudieron guardar las categorías.'],
  [/error loading categor/i, 'No se pudieron cargar las categorías.'],
  [/error loading countries/i, 'No se pudieron cargar los países.'],
  [/error loading product/i, 'No se pudo cargar el producto.'],
  [/phone must be longer than or equal to (\d+)/i, 'El teléfono debe tener al menos $1 caracteres.'],
  [/phone should not be empty/i, 'El teléfono es obligatorio.'],
  [/email must be an email/i, 'El correo no es válido.'],
  [/unauthorized/i, 'Tu sesión venció o no tienes permiso.'],
  [/internal server error/i, 'Error del servidor. Prueba de nuevo en un momento.'],
  [/http failure response/i, 'No hay conexión con el servidor. Prueba de nuevo.'],
  [/authentication failed/i, 'Correo o contraseña incorrectos.'],
  [/you must upload an image/i, 'Tienes que subir una imagen.'],
];

export function translateMessage(msg: any): string {
  if (Array.isArray(msg)) return msg.map(translateMessage).join(' ');
  let text = String(msg ?? '').trim();
  for (const [re, es] of TRANSLATIONS) {
    if (re.test(text)) { text = text.replace(re, es); break; }
  }
  return text;
}

/** Mensaje legible de un error HTTP. */
export function errorMessage(err: any, fallback = 'Algo salió mal. Prueba de nuevo.'): string {
  if (err?.status === 0) return 'No hay conexión con el servidor. Prueba de nuevo.';
  const m = err?.error?.message ?? err?.message;
  return m ? translateMessage(m) : fallback;
}

/** localStorage a prueba de navegadores que lo bloquean. */
export const prefs = {
  get<T>(key: string, fallback: T): T {
    try { const v = localStorage.getItem('dh:' + key); return v ? (JSON.parse(v) as T) : fallback; } catch { return fallback; }
  },
  set(key: string, value: any): void {
    try { localStorage.setItem('dh:' + key, JSON.stringify(value)); } catch { /* sin almacenamiento: no se recuerda */ }
  },
};
